import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuPencil, LuPlay, LuSparkles } from 'react-icons/lu';

import styles from './AiQuiz.module.css';
import { StudioQuiz } from './quizTypes';
import { writingHintKey } from './aiQuizWait';
import {
    AI_COUNTS,
    AI_DIFFICULTIES,
    AiCount,
    AiDifficulty,
    AiErrorCode,
    AiProgress,
    AiRequest,
    AI_SOURCE_MAX,
    DEFAULT_AI_REQUEST,
} from './useQuizGeneration';

/** Max, l'animateur-mascotte du Plateau (blob dégradé, même dessin que le jeu). */
export function MaxBlob({ size }: { size: number }) {
    return (
        <span className={styles.max} style={{ width: size, height: size }} aria-hidden>
            <span className={styles.maxEye} />
            <span className={styles.maxEye} />
        </span>
    );
}

/**
 * « Votre quiz en une phrase » : la phrase + idées + réglages repliés
 * (15 questions, progressif par défaut). Partagé cockpit / studio.
 */
export function AiQuizComposer({
    initial,
    onSubmit,
    compact = false,
}: {
    initial?: Partial<AiRequest>;
    onSubmit: (request: AiRequest) => void;
    compact?: boolean;
}) {
    const { t } = useTranslation();
    const [prompt, setPrompt] = useState(initial?.prompt ?? '');
    const [count, setCount] = useState<AiCount>(initial?.count ?? DEFAULT_AI_REQUEST.count);
    const [difficulty, setDifficulty] = useState<AiDifficulty>(initial?.difficulty ?? DEFAULT_AI_REQUEST.difficulty);
    const [settingsOpen, setSettingsOpen] = useState(false);
    // Texte source facultatif (sujets de niche, l'établissement lui-même…).
    const [source, setSource] = useState(initial?.source ?? '');
    // Ouvert d'office si on revient avec un texte (ou pour en ajouter un : source '').
    const [sourceOpen, setSourceOpen] = useState(initial?.source !== undefined);
    const ideas = t('animator.unquiz.ai.ideas', { returnObjects: true }) as string[];

    const submit = () => {
        if (prompt.trim())
            onSubmit({ prompt: prompt.trim(), count, difficulty, source: (sourceOpen && source.trim()) || undefined });
    };

    return (
        <div className={`${styles.card} ${compact ? styles.cardCompact : ''}`}>
            <div className={styles.cardHead}>
                {!compact && <MaxBlob size={48} />}
                <div className={styles.cardTitles}>
                    <span className={styles.kicker}>
                        <LuSparkles /> {t('animator.unquiz.ai.kicker')}
                    </span>
                    <span className={styles.cardTitle}>{t('animator.unquiz.ai.title')}</span>
                </div>
            </div>
            <label className={styles.srOnly} htmlFor={compact ? 'ai-prompt-cockpit' : 'ai-prompt-studio'}>
                {t('animator.unquiz.ai.label')}
            </label>
            <textarea
                id={compact ? 'ai-prompt-cockpit' : 'ai-prompt-studio'}
                className={styles.prompt}
                placeholder={t('animator.unquiz.ai.placeholder')}
                value={prompt}
                maxLength={200}
                rows={3}
                onChange={(e) => setPrompt(e.target.value)}
            />
            {Array.isArray(ideas) && (
                <div className={styles.ideas}>
                    {ideas.map((idea) => (
                        <button key={idea} type="button" className={styles.idea} onClick={() => setPrompt(idea)}>
                            {idea}
                        </button>
                    ))}
                </div>
            )}
            {sourceOpen ? (
                <div className={styles.source}>
                    <div className={styles.sourceLabel}>
                        <label htmlFor={compact ? 'ai-source-cockpit' : 'ai-source-studio'}>
                            {t('animator.unquiz.ai.sourceLabel')}
                        </label>
                        <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => {
                                setSourceOpen(false);
                                setSource('');
                            }}
                        >
                            {t('animator.unquiz.ai.sourceRemove')}
                        </button>
                    </div>
                    <textarea
                        id={compact ? 'ai-source-cockpit' : 'ai-source-studio'}
                        className={`${styles.prompt} ${styles.sourceArea}`}
                        placeholder={t('animator.unquiz.ai.sourcePlaceholder')}
                        value={source}
                        maxLength={AI_SOURCE_MAX}
                        rows={6}
                        onChange={(e) => setSource(e.target.value)}
                    />
                    <span className={styles.hint}>
                        {t('animator.unquiz.ai.sourceHint', { n: source.length, max: AI_SOURCE_MAX })}
                    </span>
                </div>
            ) : (
                <button type="button" className={styles.sourceAdd} onClick={() => setSourceOpen(true)}>
                    + {t('animator.unquiz.ai.sourceAdd')}
                </button>
            )}
            <div className={styles.settingsLine}>
                <span>
                    {t('animator.unquiz.ai.summary', {
                        count,
                        difficulty: t(`animator.unquiz.ai.difficulty.${difficulty}`),
                    })}
                </span>
                <button type="button" className={styles.linkBtn} onClick={() => setSettingsOpen((o) => !o)}>
                    {settingsOpen ? t('animator.unquiz.ai.close') : t('animator.unquiz.ai.modify')}
                </button>
            </div>
            {settingsOpen && (
                <div className={styles.settings}>
                    <div className={styles.chipRow}>
                        {AI_COUNTS.map((n) => (
                            <button
                                key={n}
                                type="button"
                                className={`${styles.chip} ${count === n ? styles.chipOn : ''}`}
                                onClick={() => setCount(n)}
                            >
                                {t('animator.unquiz.questions', { count: n })}
                            </button>
                        ))}
                    </div>
                    <div className={styles.chipRow}>
                        {AI_DIFFICULTIES.map((d) => (
                            <button
                                key={d}
                                type="button"
                                className={`${styles.chip} ${difficulty === d ? styles.chipOn : ''}`}
                                onClick={() => setDifficulty(d)}
                            >
                                {t(`animator.unquiz.ai.difficulty.${d}`)}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <button type="button" className={styles.cta} onClick={submit} disabled={!prompt.trim()}>
                <LuSparkles /> {t('animator.unquiz.ai.write')}
            </button>
        </div>
    );
}

/** Avancée affichée (0-100) : la barre suit les questions réellement écrites. */
export function progressPercent(p: AiProgress): number {
    if (p.phase === 'reading') return 4;
    if (p.phase === 'saving') return 96;
    return Math.round(6 + (84 * p.done) / Math.max(1, p.total));
}

/** Étapes réelles : chacune se coche en vert une fois passée. */
export function progressSteps(p: AiProgress): ('done' | 'current' | 'todo')[] {
    // Toutes écrites : le serveur relit le quiz (2e passe) → « Vérification des réponses ».
    const checking = p.phase === 'writing' && p.total > 0 && p.done >= p.total;
    const passed = p.phase === 'reading' ? 0 : checking ? 2 : p.phase === 'writing' ? 1 : 3;
    return [0, 1, 2, 3].map((i) => (i < passed ? 'done' : i === passed ? 'current' : 'todo'));
}

/** Secondes écoulées depuis l'affichage (compteur à la seconde). */
function useElapsedSeconds() {
    const [seconds, setSeconds] = useState(0);
    useEffect(() => {
        const startedAt = Date.now();
        const timer = setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
        return () => clearInterval(timer);
    }, []);
    return seconds;
}

/** Max écrit le quiz : barre et étapes suivent la génération (flux du serveur). */
export function AiQuizWriting({ prompt, progress, onCancel }: { prompt: string; progress: AiProgress; onCancel: () => void }) {
    const { t } = useTranslation();
    const seconds = useElapsedSeconds();
    const labels = [
        t('animator.unquiz.ai.stepRead'),
        progress.phase === 'writing'
            ? t('animator.unquiz.ai.stepWriteCount', { done: progress.done, total: progress.total })
            : t('animator.unquiz.ai.stepWrite'),
        t('animator.unquiz.ai.stepCheck'),
        t('animator.unquiz.ai.stepSave'),
    ];
    const states = progressSteps(progress);
    return (
        <div className={`${styles.card} ${styles.writing}`} aria-live="polite">
            <MaxBlob size={88} />
            <span className={styles.cardTitle}>{t('animator.unquiz.ai.writingTitle')}</span>
            <span className={styles.quote}>« {prompt} »</span>
            <span
                className={styles.progress}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progressPercent(progress)}
            >
                <span className={styles.progressBar} style={{ width: `${progressPercent(progress)}%` }} />
            </span>
            <ol className={styles.steps}>
                {labels.map((label, i) => (
                    <li key={i} className={`${styles.step} ${styles[`step_${states[i]}`]}`}>
                        <span className={styles.stepMark} aria-hidden>
                            {states[i] === 'done' && <LuCheck />}
                        </span>
                        {label}
                    </li>
                ))}
            </ol>
            <span className={styles.hint}>{t(`animator.unquiz.ai.${writingHintKey(seconds)}`)}</span>
            <button type="button" className={styles.ghost} onClick={onCancel}>
                {t('animator.unquiz.ai.cancel')}
            </button>
        </div>
    );
}

/** Quiz prêt : déjà enregistré dans « Mes quiz », jouable tout de suite. */
export function AiQuizReady({
    quiz,
    armed,
    onArm,
    onReview,
    onRewrite,
    showPreview = false,
    armNote,
}: {
    quiz: StudioQuiz;
    armed: boolean;
    /** Remplace l'astuce sous le bouton (ex. UnQuiz n'est pas lancé sur la TV). */
    armNote?: string;
    onArm: () => void;
    /** Absent = pas de bouton « Relire » (studio non traduit hors français). */
    onReview?: () => void;
    onRewrite: () => void;
    showPreview?: boolean;
}) {
    const { t } = useTranslation();
    return (
        <div className={styles.readyWrap}>
            <div className={styles.ready}>
                <div className={styles.readyHead}>
                    <span className={styles.readyKicker}>
                        <LuSparkles /> {t('animator.unquiz.ai.readyKicker')}
                    </span>
                    <span className={styles.readyTitle}>{quiz.title}</span>
                    {quiz.description && <span className={styles.readyDesc}>{quiz.description}</span>}
                    <span className={styles.readyMeta}>{t('animator.unquiz.questions', { count: quiz.questions.length })}</span>
                </div>
                <div className={styles.readyBody}>
                    <span className={styles.saved}>
                        <LuCheck /> {t('animator.unquiz.ai.saved')}
                    </span>
                    <button type="button" className={styles.arm} onClick={onArm} disabled={armed}>
                        {armed ? <LuCheck /> : <LuPlay />}
                        {armed ? t('animator.unquiz.ai.armed') : t('animator.unquiz.ai.arm')}
                    </button>
                    <span className={armNote ? styles.note : styles.hint}>{armNote ?? t('animator.unquiz.ai.armHint')}</span>
                </div>
            </div>
            {showPreview && (
                <div className={styles.preview}>
                    {quiz.questions.slice(0, 3).map((q, i) => (
                        <div key={q.id} className={styles.previewRow}>
                            <span className={styles.previewQ}>
                                {i + 1}. {q.question}
                            </span>
                            <span className={styles.previewA}>
                                <LuCheck /> {q.options[q.correctAnswer ?? 0]}
                            </span>
                        </div>
                    ))}
                    {quiz.questions.length > 3 && (
                        <span className={styles.hint}>
                            {t('animator.unquiz.ai.moreQuestions', { count: quiz.questions.length - 3 })}
                        </span>
                    )}
                </div>
            )}
            <div className={styles.readyActions}>
                {onReview && (
                    <button type="button" className={styles.ghost} onClick={onReview}>
                        <LuPencil /> {t('animator.unquiz.ai.review')}
                    </button>
                )}
                <button type="button" className={styles.ghost} onClick={onRewrite}>
                    {t('animator.unquiz.ai.rewrite')}
                </button>
            </div>
        </div>
    );
}

export function AiQuizError({
    code,
    detail,
    onRetry,
    onBack,
    onAddSource,
}: {
    code: AiErrorCode;
    /** Pour UNKNOWN_TOPIC : la phrase de l'IA (ce qu'elle ne connaît pas assez). */
    detail?: string;
    onRetry: () => void;
    onBack: () => void;
    /** UNKNOWN_TOPIC sans texte source : revenir à la saisie, zone de texte ouverte. */
    onAddSource?: () => void;
}) {
    const { t } = useTranslation();
    const unknown = code === 'UNKNOWN_TOPIC';
    return (
        <div className={styles.card} role="alert">
            <span className={styles.cardTitle}>
                {unknown ? t('animator.unquiz.ai.unknownTitle') : t('animator.unquiz.ai.errorTitle')}
            </span>
            {unknown && detail && <p className={styles.quote}>« {detail} »</p>}
            <p className={styles.errorText}>{t(`animator.unquiz.ai.error.${code}`)}</p>
            <div className={`${styles.readyActions} ${styles.errorActions}`}>
                {code !== 'AI_NOT_CONFIGURED' && code !== 'NO_SITE' && !unknown && (
                    <button type="button" className={styles.cta} onClick={onRetry}>
                        {t('animator.unquiz.ai.retry')}
                    </button>
                )}
                {unknown && onAddSource && (
                    <button type="button" className={styles.cta} onClick={onAddSource}>
                        + {t('animator.unquiz.ai.sourceAdd')}
                    </button>
                )}
                <button type="button" className={unknown && !onAddSource ? styles.cta : styles.ghost} onClick={onBack}>
                    {t('animator.unquiz.ai.back')}
                </button>
            </div>
        </div>
    );
}
